import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Home, Layers, Circle, Square, ArrowLeft } from 'lucide-react';

// Import step components
import {
  StepSurfacing,
  StepAdditionalCosts,
  StepEdgings,
  StepWallingFencing,
  StepMiscItems,
  StepCustomerDetails,
  StepReview
} from '@/components/quote-steps';

import API_URL from '@/config/api';
import { formatApiError } from '@/utils/apiError';
import { toast } from 'sonner';
import logger from '@/utils/logger';

const QuoteProject = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const leadId = searchParams.get('lead');
  const quoteIdParam = searchParams.get('quote');  // edit-existing mode
  const isEmbedMode = searchParams.get('embed') === 'true';

  const [costingType, setCostingType] = useState(null);
  const [projectType, setProjectType] = useState(null);
  const [step, setStep] = useState(1);
  const [leadData, setLeadData] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [savedQuoteRef, setSavedQuoteRef] = useState(null);
  const [profileLogoUrl, setProfileLogoUrl] = useState(null);
  const [profileLoaded, setProfileLoaded] = useState(false);
  // Edit-mode state: when set, we're editing an existing quote.
  // - If existing.status === 'draft' → PUT (in-place overwrite)
  // - If existing.status === 'sent'  → POST /revisions (creates v2/v3…)
  const [editingQuote, setEditingQuote] = useState(null);

  // Payment terms — captured at the review step, sent with the quote
  const [paymentTerms, setPaymentTerms] = useState(null);
  const [paymentTermsValid, setPaymentTermsValid] = useState(true);
  // Default: hide VAT wording on the quote (retail-friendly). Installer can flip
  // this on for trade / VAT-registered customers who want to see the breakdown.
  const [showVat, setShowVat] = useState(false);

  // Pre-populate "Your Details" from the user's Business Profile
  useEffect(() => {
    fetch(`${API_URL}/api/settings`, { credentials: 'include' })
      .then(res => (res.ok ? res.json() : null))
      .then(data => {
        if (!data) return;
        setCustomerDetails(prev => ({
          ...prev,
          yourName: prev.yourName || data.company_name || '',
          companyName: prev.companyName || data.company_name || '',
          yourNumber: prev.yourNumber || data.company_phone || '',
        }));
        if (data.company_logo_path) {
          setProfileLogoUrl(`${API_URL}/api/settings/logo?t=${Date.now()}`);
        }
        setProfileLoaded(true);
      })
      .catch(() => setProfileLoaded(true));
  }, []);

  // Fetch lead data if coming from a lead
  useEffect(() => {
    if (leadId) {
      fetch(`${API_URL}/api/leads/${leadId}`)
        .then(res => res.json())
        .then(data => {
          setLeadData(data);
          setCustomerDetails(prev => ({
            ...prev,
            customerName: data.customer_name || '',
            customerNumber: data.customer_phone || '',
            customerAddress: data.customer_address || ''
          }));
        })
        .catch(err => logger.error('Error fetching lead:', err));
    }
  }, [leadId]);

  // Scroll to top when step changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [step]);
  
  // Surfacing categories
  const [surfacingItems, setSurfacingItems] = useState([
    {
      id: 'vehicle_complete_probound',
      name: 'Vehicle Traffic Complete Base Build Up Using OxResin ProBound Base',
      description: 'Full base construction for driveways and vehicle areas',
      basePrice: 120,
      pricePerM2: 120,
      squareMetres: 0,
      enabled: false,
      isPremium: true,
      badge: '25 Year Manufacturer Guaranteed System'
    },
    {
      id: 'vehicle_complete_alt',
      name: 'Vehicle Traffic Complete Base Build Up Using Alternative Base',
      description: 'Full base construction for driveways and vehicle areas',
      basePrice: 105,
      pricePerM2: 105,
      squareMetres: 0,
      enabled: false
    },
    {
      id: 'foot_traffic_probound',
      name: 'Foot Traffic Complete Base Build Up Using OxResin ProBound Base',
      description: 'Paths, patios and pedestrian areas',
      basePrice: 100,
      pricePerM2: 100,
      squareMetres: 0,
      enabled: false,
      isPremium: true,
      badge: '25 Year Manufacturer Guaranteed System'
    },
    {
      id: 'foot_traffic_alt',
      name: 'Foot Traffic Complete Base Build Up Using Alternative Base',
      description: 'Paths, patios and pedestrian areas',
      basePrice: 85,
      pricePerM2: 85,
      squareMetres: 0,
      enabled: false
    },
    {
      id: 'overlays',
      name: 'Overlays',
      description: 'Overlay on existing suitable surface',
      basePrice: 55,
      pricePerM2: 55,
      squareMetres: 0,
      enabled: false
    }
  ]);

  // Additional costs
  const [additionalCosts, setAdditionalCosts] = useState({
    diggerRequired: null,
    diggerCost: 400,
    skipRequired: null,
    skipCost: 350,
    loadAwayCount: 0,
    loadAwayCostPerLoad: 350
  });

  // Edgings
  const [edgings, setEdgings] = useState([
    { id: 'concrete_pin_kerbs', name: 'Concrete Pin Kerbs', pricePerUnit: 30, unit: 'm', quantity: 0 },
    { id: 'border_block_pavers', name: 'Border: Block Pavers', pricePerUnit: 15, unit: 'm', quantity: 0 },
    { id: 'border_resin_border', name: 'Border: Resin Border', pricePerUnit: 25, unit: 'm', quantity: 0 },
    { id: 'key_kerb_small', name: 'Key Kerb - Small', pricePerUnit: 45, unit: 'm', quantity: 0 },
    { id: 'key_kerb_large', name: 'Key Kerb - Large', pricePerUnit: 65, unit: 'm', quantity: 0 },
    { id: 'key_kerb_small_radius', name: 'Key Kerb - Small - Radius', pricePerUnit: 60, unit: 'm', quantity: 0 },
    { id: 'key_kerb_large_radius', name: 'Key Kerb - Large - Radius', pricePerUnit: 80, unit: 'm', quantity: 0 },
    { id: 'sleeper_2_4m', name: '2.4m Sleeper', pricePerUnit: 60, unit: 'sleeper', quantity: 0 }
  ]);

  // Walling & Fencing
  const [wallingFencingRequired, setWallingFencingRequired] = useState(null);
  const [wallingFencingItems, setWallingFencingItems] = useState([
    { id: 'fencing_6ft', name: 'Fencing 6ft x 6ft', description: 'inc Kick Board', basePrice: 180, pricePerUnit: 180, priceIncrement: 10, unit: 'section', quantity: 0, note: '' },
    { id: 'fencing_3_4ft', name: 'Fencing 3/4ft x 3/4ft', description: 'inc Kick Board', basePrice: 140, pricePerUnit: 140, priceIncrement: 10, unit: 'section', quantity: 0, note: '' },
    { id: 'double_skin_wall', name: 'Double Skin Wall', description: 'Price per m²', basePrice: 300, pricePerUnit: 300, priceIncrement: 10, unit: 'm²', height: 0, length: 0, note: '' },
    { id: 'single_skin_wall', name: 'Single Skin Wall', description: 'Price per m²', basePrice: 200, pricePerUnit: 200, priceIncrement: 10, unit: 'm²', height: 0, length: 0, note: '' },
    { id: 'footing_for_wall', name: 'Footing for Wall', description: 'Price per metre', basePrice: 100, pricePerUnit: 100, priceIncrement: 10, unit: 'm', quantity: 0, note: '' },
    { id: 'brick_headers_copings', name: 'Brick Headers or Copings', description: 'Price per metre', basePrice: 35, pricePerUnit: 35, priceIncrement: 5, unit: 'm', quantity: 0, note: '' },
    { id: 'brick_pillar', name: 'Build Brick Pillar with Cap', description: 'Price per pillar', basePrice: 400, pricePerUnit: 400, priceIncrement: 25, unit: 'pillar', quantity: 0, width: '', height: '', note: '' }
  ]);

  // Miscellaneous Items
  const [miscItems, setMiscItems] = useState([
    { id: 'step_small_key_kerb', name: 'Step: Small Key Kerb', description: '800mm Wide, 400mm Off House', pricePerUnit: 175, unit: 'step', quantity: 0 },
    { id: 'step_large_key_kerb', name: 'Step: Large Key Kerb', description: '800mm Wide, 400mm Off House', pricePerUnit: 225, unit: 'step', quantity: 0 },
    { id: 'inspection_chamber', name: 'Inspection Chamber', pricePerUnit: 200, unit: 'item', quantity: 0 },
    { id: 'small_manhole', name: 'Small Manhole Cover', pricePerUnit: 200, unit: 'item', quantity: 0 },
    { id: 'medium_manhole', name: 'Medium Manhole Cover', description: 'up to 600 X 450', pricePerUnit: 250, unit: 'item', quantity: 0 },
    { id: 'large_manhole', name: 'Large Manhole Cover', description: 'up to 700 X 700', pricePerUnit: 300, unit: 'item', quantity: 0 },
    { id: 'inline_drain', name: 'Inline Drain / French Drain', pricePerUnit: 80, unit: 'm', quantity: 0 },
    { id: 'install_gully', name: 'Install Gully', pricePerUnit: 80, unit: 'item', quantity: 0 },
    { id: 'underground_drainage', name: 'Install Underground Drainage Pipe', pricePerUnit: 100, unit: 'm', quantity: 0 },
    { id: 'soak_away', name: 'Soak Away', description: 'including 400l crate', pricePerUnit: 300, unit: 'item', quantity: 0 }
  ]);

  // Customer details
  const [customerDetails, setCustomerDetails] = useState({
    yourName: '',
    companyName: '',
    yourNumber: '',
    companyLogo: null,
    companyLogoPreview: '',
    customerName: '',
    customerNumber: '',
    customerAddress: ''
  });

  // Edit-mode hydration: when ?quote=<id> is present, pull the existing quote
  // and pre-fill all state so the form looks like a continuation.
  useEffect(() => {
    if (!quoteIdParam) return;
    fetch(`${API_URL}/api/project-quotes/${quoteIdParam}`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((q) => {
        if (!q) return;
        setEditingQuote(q);
        setProjectType(q.quote_data?.project_type || 'resin_bound');
        setCostingType('detailed');
        if (typeof q.quote_data?.show_vat === 'boolean') setShowVat(q.quote_data.show_vat);
        if (Array.isArray(q.quote_data?.surfacing_items) && q.quote_data.surfacing_items.length > 0) {
          // Merge: keep all default rows and overlay the saved square metres on matching IDs.
          setSurfacingItems((items) =>
            items.map((i) => {
              const saved = q.quote_data.surfacing_items.find((s) => s.id === i.id);
              return saved ? { ...i, ...saved, enabled: (saved.squareMetres || 0) > 0 } : i;
            })
          );
        }
        if (q.quote_data?.additional_costs) setAdditionalCosts((prev) => ({ ...prev, ...q.quote_data.additional_costs }));
        if (Array.isArray(q.quote_data?.edgings) && q.quote_data.edgings.length > 0) {
          setEdgings((items) =>
            items.map((i) => {
              const saved = q.quote_data.edgings.find((s) => s.id === i.id);
              return saved ? { ...i, ...saved } : i;
            })
          );
        }
        setWallingFencingRequired(q.quote_data?.walling_fencing_required ?? null);
        if (Array.isArray(q.quote_data?.walling_fencing_items) && q.quote_data.walling_fencing_items.length > 0) {
          setWallingFencingItems((items) =>
            items.map((i) => {
              const saved = q.quote_data.walling_fencing_items.find((s) => s.id === i.id);
              return saved ? { ...i, ...saved } : i;
            })
          );
        }
        if (Array.isArray(q.quote_data?.misc_items) && q.quote_data.misc_items.length > 0) {
          setMiscItems((items) =>
            items.map((i) => {
              const saved = q.quote_data.misc_items.find((s) => s.id === i.id);
              return saved ? { ...i, ...saved } : i;
            })
          );
        }
        setCustomerDetails((prev) => ({
          ...prev,
          yourName: prev.yourName || q.installer_name || '',
          companyName: prev.companyName || q.company_name || '',
          yourNumber: prev.yourNumber || q.installer_phone || '',
          customerName: q.customer_name || prev.customerName,
          customerNumber: q.customer_phone || prev.customerNumber,
          customerAddress: q.customer_address || prev.customerAddress,
        }));
        if (q.payment_terms) setPaymentTerms(q.payment_terms);
      })
      .catch((e) => logger.error('Failed to load quote for editing:', e));
  }, [quoteIdParam]);

  // Handlers
  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setCustomerDetails(prev => ({
          ...prev,
          companyLogo: file,
          companyLogoPreview: reader.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handlePriceChange = (id, increment) => {
    setSurfacingItems(items => 
      items.map(item => 
        item.id === id 
          ? { ...item, pricePerM2: Math.max(0, item.pricePerM2 + increment) }
          : item
      )
    );
  };

  const handleBlendChange = (id, blend) => {
    setSurfacingItems((items) => items.map((i) => (i.id === id ? { ...i, blend } : i)));
  };

  const handleSquareMetresChange = (id, value) => {
    const numValue = parseFloat(value) || 0;
    setSurfacingItems(items => 
      items.map(item => 
        item.id === id 
          ? { ...item, squareMetres: numValue, enabled: numValue > 0 }
          : item
      )
    );
  };

  const handleAdditionalCostChange = (field, value) => {
    setAdditionalCosts(prev => ({ ...prev, [field]: value }));
  };

  const handleEdgingChange = (id, value) => {
    const numValue = parseFloat(value) || 0;
    setEdgings(items => 
      items.map(item => 
        item.id === id 
          ? { ...item, quantity: numValue }
          : item
      )
    );
  };

  const handleWallingFencingChange = (id, field, value) => {
    setWallingFencingItems(items => 
      items.map(item => {
        if (item.id !== id) return item;
        if (field === 'pricePerUnit') {
          return { ...item, pricePerUnit: Math.max(0, value) };
        }
        if (field === 'quantity' || field === 'height' || field === 'length') {
          return { ...item, [field]: parseFloat(value) || 0 };
        }
        return { ...item, [field]: value };
      })
    );
  };

  const handleMiscItemChange = (id, value) => {
    const numValue = parseFloat(value) || 0;
    setMiscItems(items => 
      items.map(item => 
        item.id === id 
          ? { ...item, quantity: numValue }
          : item
      )
    );
  };

  const handleCustomerChange = (field, value) => {
    setCustomerDetails(prev => ({ ...prev, [field]: value }));
  };

  // Calculate total excavated area
  const getTotalExcavatedArea = () => {
    const excavatedItems = surfacingItems.filter(item => 
      (item.id === 'vehicle_complete' || item.id === 'foot_traffic') && item.squareMetres > 0
    );
    return excavatedItems.reduce((sum, item) => sum + item.squareMetres, 0);
  };

  const suggestedLoadAway = Math.ceil(getTotalExcavatedArea() / 45);

  // Calculate totals
  const calculateTotals = () => {
    const itemTotals = surfacingItems
      .filter(item => item.squareMetres > 0)
      .map(item => ({
        ...item,
        subtotal: item.pricePerM2 * item.squareMetres
      }));
    
    const surfacingTotal = itemTotals.reduce((sum, item) => sum + item.subtotal, 0);
    const totalSquareMetres = itemTotals.reduce((sum, item) => sum + item.squareMetres, 0);
    
    const diggerTotal = additionalCosts.diggerRequired ? additionalCosts.diggerCost : 0;
    const skipTotal = additionalCosts.skipRequired ? additionalCosts.skipCost : 0;
    const loadAwayTotal = additionalCosts.loadAwayCount * additionalCosts.loadAwayCostPerLoad;
    const additionalTotal = diggerTotal + skipTotal + loadAwayTotal;

    const edgingTotals = edgings
      .filter(item => item.quantity > 0)
      .map(item => ({ ...item, subtotal: item.pricePerUnit * item.quantity }));
    const edgingsTotal = edgingTotals.reduce((sum, item) => sum + item.subtotal, 0);

    const wallingFencingTotals = wallingFencingItems
      .filter(item => {
        if (item.unit === 'm²') {
          return (item.height || 0) > 0 && (item.length || 0) > 0;
        }
        return (item.quantity || 0) > 0;
      })
      .map(item => {
        let subtotal = 0;
        let calculatedArea = 0;
        if (item.unit === 'm²') {
          calculatedArea = (item.height || 0) * (item.length || 0);
          subtotal = item.pricePerUnit * calculatedArea;
        } else {
          subtotal = item.pricePerUnit * (item.quantity || 0);
        }
        return { ...item, calculatedArea, subtotal };
      });
    const wallingFencingTotal = wallingFencingTotals.reduce((sum, item) => sum + item.subtotal, 0);

    const miscTotals = miscItems
      .filter(item => item.quantity > 0)
      .map(item => ({ ...item, subtotal: item.pricePerUnit * item.quantity }));
    const miscTotal = miscTotals.reduce((sum, item) => sum + item.subtotal, 0);
    
    const grandTotal = surfacingTotal + additionalTotal + edgingsTotal + wallingFencingTotal + miscTotal;
    
    return { 
      itemTotals, surfacingTotal, totalSquareMetres,
      diggerTotal, skipTotal, loadAwayTotal, additionalTotal,
      edgingTotals, edgingsTotal,
      wallingFencingTotals, wallingFencingTotal,
      miscTotals, miscTotal, grandTotal 
    };
  };

  const totals = calculateTotals();
  const hasItems = totals.itemTotals.length > 0;

  // Save quote handler — supports three modes:
  //   1. New quote → POST /project-quotes
  //   2. Editing a draft → PUT /project-quotes/{id} (in-place overwrite)
  //   3. Editing a sent quote → POST /project-quotes/{id}/revisions (creates v2/v3…)
  //
  // `sendNow=true` chains a POST /send afterwards.
  const handleSaveQuote = async (sendNow = false) => {
    setIsSaving(true);
    try {
      const quotePayload = {
        lead_id: leadId || editingQuote?.lead_id || null,
        installer_name: customerDetails.yourName || 'Unknown Installer',
        company_name: customerDetails.companyName || null,
        installer_phone: customerDetails.yourNumber || null,
        company_logo: customerDetails.companyLogoPreview || null,
        customer_name: customerDetails.customerName || null,
        customer_phone: customerDetails.customerNumber || null,
        customer_email: leadData?.customer_email || editingQuote?.customer_email || null,
        customer_address: customerDetails.customerAddress || null,
        quote_data: {
          project_type: projectType,
          surfacing_items: surfacingItems.filter((i) => i.squareMetres > 0),
          additional_costs: additionalCosts,
          edgings: edgings.filter((i) => i.quantity > 0),
          walling_fencing_required: wallingFencingRequired,
          walling_fencing_items: wallingFencingItems.filter((i) =>
            (i.unit === 'm²' ? ((i.height || 0) > 0 && (i.length || 0) > 0) : (i.quantity || 0) > 0)
          ),
          misc_items: miscItems.filter((i) => i.quantity > 0),
          totals: totals,
          show_vat: showVat,
        },
        grand_total: totals.grandTotal,
        payment_terms: paymentTerms,
        status: 'draft',
      };

      let response;
      let saved;
      if (editingQuote && editingQuote.status === 'draft') {
        // Mode 2: in-place edit
        response = await fetch(`${API_URL}/api/project-quotes/${editingQuote.id}`, {
          method: 'PUT',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(quotePayload),
        });
      } else if (editingQuote && editingQuote.status !== 'draft') {
        // Mode 3: new revision based on the sent quote
        response = await fetch(`${API_URL}/api/project-quotes/${editingQuote.id}/revisions`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...quotePayload, parent_quote_id: editingQuote.id }),
        });
      } else {
        // Mode 1: brand-new quote
        response = await fetch(`${API_URL}/api/project-quotes`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(quotePayload),
        });
      }

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(formatApiError(err) || 'Save failed');
      }
      saved = await response.json();
      setEditingQuote(saved);
      setSavedQuoteRef(saved.reference + (sendNow ? ' (sent)' : ''));

      if (sendNow) {
        const sendRes = await fetch(`${API_URL}/api/project-quotes/${saved.id}/send`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({}),
        });
        const sendData = await sendRes.json().catch(() => ({}));
        if (!sendRes.ok || !sendData.sent) {
          alert(`Quote saved as ${saved.reference}, but the email failed to send (check Resend logs).`);
        }
      }
      return saved;
    } catch (error) {
      logger.error('Error saving quote:', error);
      alert(`Error saving quote: ${error.message}`);
      return null;
    } finally {
      setIsSaving(false);
    }
  };

  // "Order these materials" — turns the linked material price into a confirmed Ox order + marks this quote accepted.
  const handleOrderMaterials = async () => {
    const mid = editingQuote?.material_quote_id;
    if (!mid) return;
    if (!window.confirm('Place a confirmed order with Ox Resin for these materials? This also marks the quote as accepted by the customer.')) return;
    try {
      const res = await fetch(`${API_URL}/api/quotes/${mid}/place-order`, { method: 'POST', credentials: 'include' });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(formatApiError(body) || 'Could not place the order');
      const fresh = await fetch(`${API_URL}/api/project-quotes/${editingQuote.id}`, { credentials: 'include' });
      if (fresh.ok) setEditingQuote(await fresh.json());
      toast.success(`Order ${body.reference} placed with Ox Resin — quote marked accepted`);
    } catch (e) { toast.error(e.message); }
  };

  // "Get material price" — saves a draft if needed, then opens the material calculator pre-filled from this quote.
  const handleGetMaterialPrice = async () => {
    let id = editingQuote?.id;
    if (!id) {
      const saved = await handleSaveQuote(false);
      id = saved?.id;
    }
    if (id) navigate(`/order?quote=${id}`);
  };

  const projectTypes = [
    { id: 'resin_bound', name: 'Resin Bound Stone', description: 'Traditional resin bound aggregate surfacing for driveways, paths and patios', icon: Layers, available: true },
    { id: 'rubber_crumb', name: 'Rubber Crumb', description: 'Recycled rubber surfacing for play areas and sports surfaces', icon: Circle, available: false },
    { id: 'savoro', name: 'Savoro - Internal Resin Bound Flooring', description: 'Decorative internal flooring solution', icon: Square, available: false }
  ];

  return (
    <div className="min-h-screen bg-[#E8ECEF]">
      {/* Header - Hidden in embed mode */}
      {!isEmbedMode && (
        <header className="bg-white shadow-sm border-b-4 border-[#300122]">
          <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
            <Button
              onClick={() => navigate('/my-quotes')}
              variant="outline"
              className="border-2 border-[#2F5E96] text-[#2F5E96] hover:bg-[#2F5E96]/10 font-medium"
              data-testid="header-back-to-myquotes"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Customer Quotes
            </Button>
            <img 
              src="https://customer-assets.emergentagent.com/job_resin-quote-calc/artifacts/0yxgnasw_IMG_8077.jpeg" 
              alt="Ox Resin ProBound" 
              className="h-12 md:h-14 cursor-pointer"
              onClick={() => navigate('/')}
            />
          </div>
        </header>
      )}

      {/* Progress Bar */}
      {costingType && projectType && (
        <div className="bg-white border-b">
          <div className="max-w-4xl mx-auto px-4 py-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-500">Step {step} of 7</span>
              <span className="text-[#300122] font-medium">{Math.round((step / 7) * 100)}%</span>
            </div>
            <div className="mt-2 h-2 bg-gray-200 rounded-full overflow-hidden">
              <div className="h-full bg-[#300122] transition-all duration-300" style={{ width: `${(step / 7) * 100}%` }} />
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-6 md:py-8">
        {/* Lead Banner */}
        {leadData && (
          <div className="mb-6 p-4 bg-[#2F5E96]/10 border border-[#2F5E96]/30 rounded-xl">
            <p className="text-sm text-[#2F5E96] font-medium">
              Creating quote for: <strong>{leadData.customer_name}</strong>
              {leadData.customer_address && ` • ${leadData.customer_address}`}
            </p>
          </div>
        )}

        {/* Section Title */}
        <div className="flex flex-col items-center mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-[#27292E]">Start a New Quote</h1>
        </div>

        {/* Project Type Selection */}
        {!projectType && (
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 md:p-8">
            <h2 className="text-xl md:text-2xl font-bold text-[#27292E] mb-2 text-center">
              What type of project is this?
            </h2>
            <p className="text-gray-600 mb-8 text-center">
              Select the surfacing type for this project.
            </p>
            <div className="space-y-4">
              {projectTypes.map((type) => {
                const IconComponent = type.icon;
                return (
                  <button
                    key={type.id}
                    onClick={() => { if (type.available) { setProjectType(type.id); setStep(1); }}}
                    disabled={!type.available}
                    className={`w-full p-6 border-2 rounded-xl text-left transition-all ${
                      type.available 
                        ? 'border-gray-200 hover:border-[#300122] hover:bg-[#300122]/5 cursor-pointer' 
                        : 'border-gray-100 bg-gray-50 cursor-not-allowed opacity-60'
                    }`}
                    data-testid={`project-type-${type.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`rounded-xl p-3 ${type.available ? 'bg-[#300122]/10' : 'bg-gray-200'}`}>
                        <IconComponent className={`w-6 h-6 ${type.available ? 'text-[#300122]' : 'text-gray-400'}`} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="text-lg font-bold text-[#27292E]">{type.name}</h3>
                          {!type.available && (
                            <span className="text-xs bg-gray-200 text-gray-500 px-2 py-0.5 rounded-full">Coming Soon</span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 mt-1">{type.description}</p>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Steps Wizard */}
        {projectType === 'resin_bound' && (
          <>
            {/* Step Progress Indicator */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-4 mb-6" data-testid="step-progress">
              <div className="flex justify-between items-center mb-3">
                <span className="text-sm font-medium text-[#27292E]">Step {step} of 7</span>
                <span className="text-sm font-medium text-[#2F5E96]">{Math.round((step / 7) * 100)}%</span>
              </div>
              <div className="flex gap-1.5">
                {[
                  { num: 1, label: 'Surfacing' },
                  { num: 2, label: 'Additional' },
                  { num: 3, label: 'Edgings' },
                  { num: 4, label: 'Walling' },
                  { num: 5, label: 'Misc' },
                  { num: 6, label: 'Details' },
                  { num: 7, label: 'Review' }
                ].map((s) => (
                  <div key={s.num} className="flex-1 flex flex-col items-center gap-1">
                    <div className={`w-full h-2 rounded-full transition-all duration-300 ${s.num <= step ? 'bg-[#2F5E96]' : 'bg-gray-200'}`} />
                    <span className={`text-[10px] font-medium hidden sm:block ${s.num === step ? 'text-[#2F5E96]' : s.num < step ? 'text-[#27292E]' : 'text-gray-400'}`}>
                      {s.label}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Step 1: Surfacing */}
            {step === 1 && (
              <StepSurfacing
                surfacingItems={surfacingItems}
                onPriceChange={handlePriceChange}
                onSquareMetresChange={handleSquareMetresChange}
                onBlendChange={handleBlendChange}
                totals={totals}
                hasItems={hasItems}
                onBack={() => { setCostingType(null); setStep(1); }}
                onContinue={() => setStep(2)}
              />
            )}

            {/* Step 2: Additional Costs */}
            {step === 2 && (
              <StepAdditionalCosts
                additionalCosts={additionalCosts}
                onAdditionalCostChange={handleAdditionalCostChange}
                totals={totals}
                suggestedLoadAway={suggestedLoadAway}
                totalExcavatedArea={getTotalExcavatedArea()}
                onBack={() => setStep(1)}
                onContinue={() => setStep(3)}
              />
            )}

            {/* Step 3: Edgings */}
            {step === 3 && (
              <StepEdgings
                edgings={edgings}
                onEdgingChange={handleEdgingChange}
                totals={totals}
                onBack={() => setStep(2)}
                onContinue={() => setStep(4)}
              />
            )}

            {/* Step 4: Walling & Fencing */}
            {step === 4 && (
              <StepWallingFencing
                wallingFencingRequired={wallingFencingRequired}
                setWallingFencingRequired={setWallingFencingRequired}
                wallingFencingItems={wallingFencingItems}
                onWallingFencingChange={handleWallingFencingChange}
                totals={totals}
                onBack={() => setStep(3)}
                onContinue={() => setStep(5)}
              />
            )}

            {/* Step 5: Miscellaneous Items */}
            {step === 5 && (
              <StepMiscItems
                miscItems={miscItems}
                onMiscItemChange={handleMiscItemChange}
                totals={totals}
                onBack={() => setStep(4)}
                onContinue={() => setStep(6)}
              />
            )}

            {/* Step 6: Customer Details */}
            {step === 6 && (
              <StepCustomerDetails
                customerDetails={customerDetails}
                onCustomerChange={handleCustomerChange}
                onLogoUpload={handleLogoUpload}
                profileLogoUrl={profileLogoUrl}
                profileLoaded={profileLoaded}
                totals={totals}
                onBack={() => setStep(5)}
                onContinue={() => setStep(7)}
              />
            )}

            {/* Step 7: Review */}
            {step === 7 && (
              <StepReview
                projectType={projectType}
                customerDetails={customerDetails}
                totals={totals}
                additionalCosts={additionalCosts}
                wallingFencingRequired={wallingFencingRequired}
                savedQuoteRef={savedQuoteRef}
                isSaving={isSaving}
                onBack={() => setStep(6)}
                onSave={handleSaveQuote}
                onGetMaterialPrice={handleGetMaterialPrice}
                onOrderMaterials={handleOrderMaterials}
                materialLink={editingQuote}
                surfacingItems={surfacingItems.filter(i => i.squareMetres > 0)}
                edgings={edgings.filter(i => i.quantity > 0)}
                wallingFencingItems={wallingFencingItems.filter(i => 
                  (i.unit === 'm²' ? ((i.height || 0) > 0 && (i.length || 0) > 0) : (i.quantity || 0) > 0)
                )}
                miscItems={miscItems.filter(i => i.quantity > 0)}
                grandTotal={totals.grandTotal}
                paymentTerms={paymentTerms}
                onPaymentTermsChange={setPaymentTerms}
                onPaymentTermsValidityChange={setPaymentTermsValid}
                paymentTermsValid={paymentTermsValid}
                showVat={showVat}
                onShowVatChange={setShowVat}
              />
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default QuoteProject;
